import React, { useState, useEffect } from 'react';
import type { CSSProperties } from 'react';
import type { SDUIComponentProps } from '../../renderer/ComponentRenderer';
import { cn } from '@/lib/utils';
import { useArcheAction } from '../../hooks/useArcheAction';

const inputBaseClasses =
  'w-full px-3 py-2 bg-background border border-input rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent disabled:bg-muted disabled:cursor-not-allowed';

/**
 * TextInput component - single-line text input.
 */
export function TextInput({ id, props, style }: SDUIComponentProps) {
  const { updateInput } = useArcheAction();

  const {
    name,
    label,
    placeholder,
    value: initialValue = '',
    required = false,
    disabled = false,
    type = 'text',
    onChange,
    className,
  } = props as {
    name?: string;
    label?: string;
    placeholder?: string;
    value?: string;
    required?: boolean;
    disabled?: boolean;
    type?: 'text' | 'email' | 'password' | 'tel' | 'url';
    onChange?: () => void;
    className?: string;
  };

  const [value, setValue] = useState(initialValue);
  const inputKey = name || id;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    updateInput(inputKey, newValue);
    if (onChange) onChange();
  };

  // Sync initial value to context
  useEffect(() => {
    if (initialValue) {
      updateInput(inputKey, initialValue);
    }
  }, [inputKey, initialValue, updateInput]);

  return (
    <div className={className} style={style as CSSProperties}>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-foreground mb-1">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
      )}
      <input
        id={id}
        type={type}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        className={inputBaseClasses}
      />
    </div>
  );
}

/**
 * TextArea component - multi-line text input.
 */
export function TextArea({ id, props, style }: SDUIComponentProps) {
  const { updateInput } = useArcheAction();

  const {
    name,
    label,
    placeholder,
    value: initialValue = '',
    required = false,
    disabled = false,
    rows = 4,
    onChange,
    className,
  } = props as {
    name?: string;
    label?: string;
    placeholder?: string;
    value?: string;
    required?: boolean;
    disabled?: boolean;
    rows?: number;
    onChange?: () => void;
    className?: string;
  };

  const [value, setValue] = useState(initialValue);
  const inputKey = name || id;

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    updateInput(inputKey, newValue);
    if (onChange) onChange();
  };

  // Sync initial value to context
  useEffect(() => {
    if (initialValue) {
      updateInput(inputKey, initialValue);
    }
  }, [inputKey, initialValue, updateInput]);

  return (
    <div className={className} style={style as CSSProperties}>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-foreground mb-1">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
      )}
      <textarea
        id={id}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        rows={rows}
        className={cn(inputBaseClasses, 'resize-y')}
      />
    </div>
  );
}

/**
 * NumberInput component - numeric input with optional min/max/step.
 */
export function NumberInput({ id, props, style }: SDUIComponentProps) {
  const { updateInput } = useArcheAction();

  const {
    name,
    label,
    placeholder,
    value: initialValue = '',
    required = false,
    disabled = false,
    min,
    max,
    step = 1,
    onChange,
    className,
  } = props as {
    name?: string;
    label?: string;
    placeholder?: string;
    value?: string | number;
    required?: boolean;
    disabled?: boolean;
    min?: number;
    max?: number;
    step?: number;
    onChange?: () => void;
    className?: string;
  };

  const [value, setValue] = useState(String(initialValue));
  const inputKey = name || id;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    updateInput(inputKey, newValue === '' ? '' : Number(newValue));
    if (onChange) onChange();
  };

  // Sync initial value to context
  useEffect(() => {
    if (initialValue !== '') {
      updateInput(inputKey, Number(initialValue));
    }
  }, [inputKey, initialValue, updateInput]);

  return (
    <div className={className} style={style as CSSProperties}>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-foreground mb-1">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
      )}
      <input
        id={id}
        type="number"
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        min={min}
        max={max}
        step={step}
        className={inputBaseClasses}
      />
    </div>
  );
}

/**
 * Select component - dropdown selection.
 */
export function Select({ id, props, style }: SDUIComponentProps) {
  const { updateInput } = useArcheAction();

  const {
    name,
    label,
    options = [],
    value: initialValue = '',
    required = false,
    disabled = false,
    placeholder,
    onChange,
    className,
  } = props as {
    name?: string;
    label?: string;
    options?: Array<{ value: string; label: string }>;
    value?: string;
    required?: boolean;
    disabled?: boolean;
    placeholder?: string;
    onChange?: () => void;
    className?: string;
  };

  const [value, setValue] = useState(initialValue);
  const inputKey = name || id;

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    updateInput(inputKey, newValue);
    if (onChange) onChange();
  };

  // Sync initial value to context
  useEffect(() => {
    if (initialValue) {
      updateInput(inputKey, initialValue);
    }
  }, [inputKey, initialValue, updateInput]);

  return (
    <div className={className} style={style as CSSProperties}>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-foreground mb-1">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
      )}
      <select
        id={id}
        value={value}
        onChange={handleChange}
        required={required}
        disabled={disabled}
        className={inputBaseClasses}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * Checkbox component - boolean toggle.
 */
export function Checkbox({ id, props, style }: SDUIComponentProps) {
  const { updateInput } = useArcheAction();

  const {
    name,
    label,
    checked: initialChecked = false,
    disabled = false,
    onChange,
    className,
  } = props as {
    name?: string;
    label?: string;
    checked?: boolean;
    disabled?: boolean;
    onChange?: () => void;
    className?: string;
  };

  const [checked, setChecked] = useState(initialChecked);
  const inputKey = name || id;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.checked;
    setChecked(newValue);
    updateInput(inputKey, newValue);
    if (onChange) onChange();
  };

  // Sync initial value to context
  useEffect(() => {
    updateInput(inputKey, initialChecked);
  }, [inputKey, initialChecked, updateInput]);

  return (
    <div className={cn('flex items-center', className)} style={style as CSSProperties}>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={handleChange}
        disabled={disabled}
        className="h-4 w-4 text-primary focus:ring-ring border-input rounded disabled:cursor-not-allowed"
      />
      {label && (
        <label htmlFor={id} className="ml-2 text-sm text-foreground">
          {label}
        </label>
      )}
    </div>
  );
}

/**
 * RadioGroup component - single selection from options.
 */
export function RadioGroup({ id, props, style }: SDUIComponentProps) {
  const { updateInput } = useArcheAction();

  const {
    name,
    label,
    options = [],
    value: initialValue = '',
    disabled = false,
    direction = 'vertical',
    onChange,
    className,
  } = props as {
    name?: string;
    label?: string;
    options?: Array<{ value: string; label: string }>;
    value?: string;
    disabled?: boolean;
    direction?: 'horizontal' | 'vertical';
    onChange?: () => void;
    className?: string;
  };

  const [value, setValue] = useState(initialValue);
  const inputKey = name || id;

  const handleChange = (optionValue: string) => {
    setValue(optionValue);
    updateInput(inputKey, optionValue);
    if (onChange) onChange();
  };

  // Sync initial value to context
  useEffect(() => {
    if (initialValue) {
      updateInput(inputKey, initialValue);
    }
  }, [inputKey, initialValue, updateInput]);

  return (
    <div className={className} style={style as CSSProperties}>
      {label && <label className="block text-sm font-medium text-foreground mb-2">{label}</label>}
      <div className={direction === 'horizontal' ? 'flex space-x-4' : 'space-y-2'}>
        {options.map((option) => (
          <div key={option.value} className="flex items-center">
            <input
              id={`${id}-${option.value}`}
              type="radio"
              name={id}
              value={option.value}
              checked={value === option.value}
              onChange={() => handleChange(option.value)}
              disabled={disabled}
              className="h-4 w-4 text-primary focus:ring-ring border-input disabled:cursor-not-allowed"
            />
            <label htmlFor={`${id}-${option.value}`} className="ml-2 text-sm text-foreground">
              {option.label}
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * FileInput component - upload new files.
 */
export function FileInput({ id, props, style }: SDUIComponentProps) {
  const { updateInput } = useArcheAction();

  const {
    name,
    label,
    accept,
    maxSize = 10 * 1024 * 1024, // 10MB default
    multiple = false,
    required = false,
    disabled = false,
    className,
  } = props as {
    name?: string;
    label?: string;
    accept?: string;
    maxSize?: number;
    multiple?: boolean;
    required?: boolean;
    disabled?: boolean;
    className?: string;
  };

  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const inputKey = name || id;
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    setError(null);

    // Validate file sizes
    const oversizedFiles = selectedFiles.filter((f) => f.size > maxSize);
    if (oversizedFiles.length > 0) {
      setError(`Files exceed maximum size of ${formatFileSize(maxSize)}`);
      return;
    }

    setFiles(selectedFiles);

    // Update context with file info (not the actual File objects)
    updateInput(
      inputKey,
      selectedFiles.map((f) => ({
        name: f.name,
        size: f.size,
        type: f.type,
      })),
    );
  };

  const removeFile = (index: number) => {
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);
    updateInput(
      inputKey,
      newFiles.map((f) => ({
        name: f.name,
        size: f.size,
        type: f.type,
      })),
    );
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  return (
    <div className={className} style={style as CSSProperties}>
      {label && (
        <label className="block text-sm font-medium text-foreground mb-1">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
      )}

      <div
        onClick={() => !disabled && inputRef.current?.click()}
        className={cn(
          'border-2 border-dashed rounded-md p-4 text-center cursor-pointer transition-colors',
          disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-primary/50',
          error ? 'border-destructive' : 'border-input',
        )}
      >
        <input
          ref={inputRef}
          id={id}
          type="file"
          accept={accept}
          multiple={multiple}
          required={required}
          disabled={disabled}
          onChange={handleFileChange}
          className="hidden"
        />
        <p className="text-sm text-muted-foreground">
          {files.length > 0
            ? `${files.length} file(s) selected`
            : 'Click to upload or drag and drop'}
        </p>
        <p className="text-xs text-muted-foreground mt-1">Max size: {formatFileSize(maxSize)}</p>
      </div>

      {error && <p className="text-sm text-destructive mt-1">{error}</p>}

      {files.length > 0 && (
        <ul className="mt-2 space-y-1">
          {files.map((file, index) => (
            <li
              key={index}
              className="flex items-center justify-between text-sm bg-muted px-2 py-1 rounded"
            >
              <span className="truncate">{file.name}</span>
              <button
                type="button"
                onClick={() => removeFile(index)}
                className="ml-2 text-muted-foreground hover:text-destructive"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * FilePicker component - select from existing user files.
 */
export function FilePicker({ id, props, style }: SDUIComponentProps) {
  const { updateInput } = useArcheAction();

  const {
    name,
    label,
    categories,
    selectionMode = 'single',
    required = false,
    disabled = false,
    className,
  } = props as {
    name?: string;
    label?: string;
    categories?: string[];
    selectionMode?: 'single' | 'multiple';
    required?: boolean;
    disabled?: boolean;
    className?: string;
  };

  const [selectedFiles, setSelectedFiles] = useState<Array<{ fileId: string; fileName: string }>>(
    [],
  );
  const [isOpen, setIsOpen] = useState(false);
  const inputKey = name || id;

  const handleSelectFile = (file: { fileId: string; fileName: string }) => {
    let newSelection: typeof selectedFiles;

    if (selectionMode === 'single') {
      newSelection = [file];
      setIsOpen(false);
    } else {
      const isSelected = selectedFiles.some((f) => f.fileId === file.fileId);
      if (isSelected) {
        newSelection = selectedFiles.filter((f) => f.fileId !== file.fileId);
      } else {
        newSelection = [...selectedFiles, file];
      }
    }

    setSelectedFiles(newSelection);
    updateInput(
      inputKey,
      selectionMode === 'single' ? newSelection[0]?.fileId : newSelection.map((f) => f.fileId),
    );
  };

  // Mark function as used for future FileBrowser integration
  void handleSelectFile;

  const removeFile = (fileId: string) => {
    const newSelection = selectedFiles.filter((f) => f.fileId !== fileId);
    setSelectedFiles(newSelection);
    updateInput(
      inputKey,
      selectionMode === 'single' ? newSelection[0]?.fileId : newSelection.map((f) => f.fileId),
    );
  };

  return (
    <div className={className} style={style as CSSProperties}>
      {label && (
        <label className="block text-sm font-medium text-foreground mb-1">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
      )}

      <button
        type="button"
        onClick={() => !disabled && setIsOpen(true)}
        disabled={disabled}
        className={cn(inputBaseClasses, 'text-left', disabled && 'cursor-not-allowed')}
      >
        {selectedFiles.length > 0
          ? `${selectedFiles.length} file(s) selected`
          : 'Click to select files...'}
      </button>

      {selectedFiles.length > 0 && (
        <ul className="mt-2 space-y-1">
          {selectedFiles.map((file) => (
            <li
              key={file.fileId}
              className="flex items-center justify-between text-sm bg-muted px-2 py-1 rounded"
            >
              <span className="truncate">{file.fileName}</span>
              <button
                type="button"
                onClick={() => removeFile(file.fileId)}
                className="ml-2 text-muted-foreground hover:text-destructive"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* File picker modal would be implemented here */}
      {/* For now, this is a placeholder that integrates with FileBrowser component */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background rounded-lg p-4 max-w-2xl w-full max-h-[80vh] overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Select Files</h3>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                ×
              </button>
            </div>
            <p className="text-sm text-muted-foreground">
              File picker content - integrate with FileBrowser component
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              Categories: {categories?.join(', ') || 'all'}
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="px-4 py-2 text-sm border rounded-md hover:bg-muted"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
